import React from 'react';
import { trustBadges } from '../data/mock';
import { ShieldCheck, MapPin, Clock, Heart } from 'lucide-react';

const iconMap = {
  ShieldCheck,
  MapPin,
  Clock,
  Heart
};

const TrustSection = () => {
  return (
    <section id="about" className="py-16 md:py-24 bg-white" aria-label="Why Choose Dog Boarding Baltimore">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-amber-100 text-amber-700 font-semibold text-sm px-4 py-1.5 rounded-full mb-4">
            Why Choose Us
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
            Why Baltimore Families Trust Us for Dog Boarding
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            We're not just another <strong>dog boarding facility in Baltimore</strong>. We're your neighbors, committed to treating every dog like family with our 100% background-checked staff.
          </p>
        </div>

        {/* Trust Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustBadges.map((badge) => {
            const IconComponent = iconMap[badge.icon];
            return (
              <div
                key={badge.id}
                className="group bg-stone-50 hover:bg-amber-50 rounded-2xl p-6 border border-stone-100 hover:border-amber-200 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-14 h-14 bg-amber-100 group-hover:bg-amber-500 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300">
                  <IconComponent className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-stone-800 mb-2">{badge.title}</h3>
                <p className="text-stone-600 leading-relaxed">{badge.description}</p>
              </div>
            );
          })}
        </div>

        {/* Local Business Highlight */}
        <div className="mt-16 bg-gradient-to-r from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 border border-amber-100">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-stone-800 mb-4">
                Locally Owned Dog Boarding in Baltimore, MD
              </h3>
              <p className="text-stone-600 leading-relaxed mb-6">
                Unlike big corporate chain kennels, we're a <strong>Baltimore family-owned dog boarding business</strong>. We know this city, we love our community, and we're committed to providing personalized pet care that only a local business can offer. When you call, you'll talk to someone who truly cares about your dog. We serve Federal Hill, Canton, Fells Point, Hampden, Roland Park, Towson, and all Baltimore neighborhoods.
              </p>
              <ul className="space-y-3">
                {[
                  'Same friendly faces every visit',
                  'Personalized attention for every dog',
                  'Deep roots in Baltimore neighborhoods',
                  'Recommended by your neighbors'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-stone-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1174081/pexels-photo-1174081.jpeg"
                alt="Professional dog care"
                className="rounded-2xl shadow-xl w-full h-[300px] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg px-4 py-3">
                <p className="text-amber-600 font-bold text-lg">5+ Years</p>
                <p className="text-stone-500 text-sm">Serving Baltimore</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
